import { InfoRounded } from "@mui/icons-material";
import { Box, Popover, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { ICellRendererParams } from "ag-grid-community";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { getAncillaries, getCategories, getLocations, getProducts, getRates, getSalesChannels, getSegments } from "../../../../api/services";
import useMousePopper from "../../../../hooks/useMousePopper";
import { IRule, ReactQueryKey, RuleType } from "../../../../types";
import { rulesFilterState } from "../../recoil";
import { useRecoilValue } from "recoil";

const CellParameters = (params: ICellRendererParams<IRule>) => {

    const { t } = useTranslation('booking');

    const mousePopper = useMousePopper({
        enterDelay: 400
    });

    const rulesFilter = useRecoilValue(rulesFilterState);
    const { tenants, dealers } = rulesFilter || {};

    const locationsQuery = useQuery({
        queryKey: [ReactQueryKey.Locations, tenants, dealers],
        queryFn: () => getLocations({tenants, dealers}),
    });
    const segmentsQuery = useQuery({
        queryKey: [ReactQueryKey.Segments, tenants, dealers],
        queryFn: () => getSegments({tenants, dealers}),
    });
    const categoriesQuery = useQuery({
        queryKey: [ReactQueryKey.Categories, tenants, dealers],
        queryFn: () => getCategories({tenants, dealers}),
    });
    const productsQuery = useQuery({
        queryKey: [ReactQueryKey.Products, tenants, dealers],
        queryFn: () => getProducts({tenants, dealers}),
    });
    const ratesQuery = useQuery({
        queryKey: [ReactQueryKey.Rates, tenants, dealers],
        queryFn: () => getRates({tenants, dealers}),
    });
    const ancillariesQuery = useQuery({
        queryKey: [ReactQueryKey.Ancillaries, tenants, dealers],
        queryFn: () => getAncillaries({tenants, dealers}),
    });
    const salesChannelsQuery = useQuery({
        queryKey: [ReactQueryKey.SalesChannels, tenants, dealers],
        queryFn: () => getSalesChannels({tenants, dealers})
    })

    if (!params.data) {
        return undefined;
    }
    const {
        pickupDateTimeFrom,
        excludedPickupDateTimeFrom,
        pickupDateTimeTo,
        excludedPickupDateTimeTo,
        dropoffDateTimeFrom,
        excludedDropoffDateTimeFrom,
        dropoffDateTimeTo,
        excludedDropoffDateTimeTo,
        includingDateTimeFrom,
        excludedIncludingDateTimeFrom,
        includingDateTimeTo,
        excludedIncludingDateTimeTo,
        pickupLocations,
        excludedPickupLocations,
        dropoffLocations,
        excludedDropoffLocations,
        pickupAreas,
        excludedPickupAreas,
        dropoffAreas,
        excludedDropoffAreas,
        pickupRegions,
        excludedPickupRegions,
        dropoffRegions,
        excludedDropoffRegions,
        pickupZones,
        excludedPickupZones,
        dropoffZones,
        excludedDropoffZones,
        acrissList,
        excludedAcrissList,
        sippList,
        excludedSippList,
        customerCodes,
        excludedCustomerCodes,
        salesChannels,
        excludedSalesChannels,
        segments,
        excludedSegments,
        categories,
        excludedCategories,
        products,
        excludedProducts,
        rates,
        excludedRates,
        ancillaries,
        excludedAncillaries,
        minDuration,
        excludedMinDuration,
        maxDuration,
        excludedMaxDuration,
        minNoticeDays,
        excludedMinNoticeDays,
        maxNoticeHours,
        excludedMaxNoticeHours,
        startDateTime,
        endDateTime,
        skip,
        skipCounter,
        applyFor,
        applyForCounter,
        type
    } = params.data;

    return (
        <>
            <Stack
                direction="row"
                alignItems="center"
                onMouseEnter={mousePopper.handleShow}
                onMouseLeave={mousePopper.handleClose}
            >
                <InfoRounded
                    color="secondary"
                />
            </Stack>
            <Popover
                open={mousePopper.open}
                anchorEl={mousePopper.anchorEl}
                onClose={mousePopper.handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                sx={{
                    pointerEvents: 'none'
                }}
            >
                <Box p={2}>
                    <Typography variant="body2">
                        {`${t('label.bookingDateFrom')}: `}
                        <b>{dayjs(startDateTime).format('DD/MM/YYYY HH:mm')}</b>
                    </Typography>
                    <Typography variant="body2">
                        {`${t('label.bookingDateTo')}: `}
                        <b>{endDateTime ? dayjs(endDateTime).format('DD/MM/YYYY HH:mm') : '-'}</b>
                    </Typography>
                    <Typography variant="body2">
                        {`${t('label.skip')}: `}
                        <b>{`${skipCounter || 0} (${skip || 0})`}</b>
                    </Typography>
                    <Typography variant="body2">
                        {`${t('label.applyFor')}: `}
                        <b>{type === RuleType.Pending ? `${applyForCounter || 0} (${applyFor || 0})` : '-'}</b>
                    </Typography>
                    {pickupDateTimeFrom &&
                        <Typography variant="body2">
                            {t('label.pickupDateTimeFrom') + (excludedPickupDateTimeFrom ? ` (${t('label.excluded')})` : '') + ': '}
                            <b>{dayjs(pickupDateTimeFrom).format('DD/MM/YYYY HH:mm')}</b>
                        </Typography>
                    }
                    {pickupDateTimeTo &&
                        <Typography variant="body2">
                            {t('label.pickupDateTimeTo') + (excludedPickupDateTimeTo ? ` (${t('label.excluded')})` : '') + ': '}
                            <b>{dayjs(pickupDateTimeTo).format('DD/MM/YYYY HH:mm')}</b>
                        </Typography>
                    }
                    {dropoffDateTimeFrom &&
                        <Typography variant="body2">
                            {t('label.dropoffDateTimeFrom') + (excludedDropoffDateTimeFrom ? ` (${t('label.excluded')})` : '') + ': '}
                            <b>{dayjs(dropoffDateTimeFrom).format('DD/MM/YYYY HH:mm')}</b>
                        </Typography>
                    }
                    {dropoffDateTimeTo &&
                        <Typography variant="body2">
                            {t('label.dropoffDateTimeTo') + (excludedDropoffDateTimeTo ? ` (${t('label.excluded')})` : '') + ': '}
                            <b>{dayjs(dropoffDateTimeTo).format('DD/MM/YYYY HH:mm')}</b>
                        </Typography>
                    }
                    {includingDateTimeFrom &&
                        <Typography variant="body2">
                            {t('label.includingDateTimeFrom') + (excludedIncludingDateTimeFrom ? ` (${t('label.excluded')})` : '') + ': '}
                            <b>{dayjs(includingDateTimeFrom).format('DD/MM/YYYY HH:mm')}</b>
                        </Typography>
                    }
                    {includingDateTimeTo &&
                        <Typography variant="body2">
                            {t('label.includingDateTimeTo') + (excludedIncludingDateTimeTo ? ` (${t('label.excluded')})` : '') + ': '}
                            <b>{dayjs(includingDateTimeTo).format('DD/MM/YYYY HH:mm')}</b>
                        </Typography>
                    }
                    {pickupLocations.length > 0 &&
                        <Typography variant="body2">
                            {t('label.pickupLocations') + (excludedPickupLocations ? ` (${t('label.excluded')})` : '') + ': '}
                            <b>
                                {pickupLocations
                                    .reduce((prev: string[], curr) => {
                                        const founded = locationsQuery.data?.locations.find(v => v.id === curr)
                                        if (founded) {
                                            prev.push(founded.label)
                                        }
                                        return prev;
                                    }, [])
                                    .join(', ')
                                }
                            </b>
                        </Typography>
                    }
                    {dropoffLocations.length > 0 &&
                        <Typography variant="body2">
                            {t('label.dropoffLocations') + (excludedDropoffLocations ? ` (${t('label.excluded')})` : '') + ': '}
                            <b>
                                {dropoffLocations
                                    .reduce((prev: string[], curr) => {
                                        const founded = locationsQuery.data?.locations.find(v => v.id === curr)
                                        if (founded) {
                                            prev.push(founded.label)
                                        }
                                        return prev;
                                    }, [])
                                    .join(', ')
                                }
                            </b>
                        </Typography>
                    }
                    {pickupAreas.length > 0 &&
                        <Typography variant="body2">
                            {t('label.pickupAreas') + (excludedPickupAreas ? ` (${t('label.excluded')})` : '') + ': '}
                            <b>{pickupAreas.map((a, index, arr) => index === arr.length - 1 ? a : `${a}, `)}</b>
                        </Typography>
                    }
                    {dropoffAreas.length > 0 &&
                        <Typography variant="body2">
                            {t('label.dropoffAreas') + (excludedDropoffAreas ? ` (${t('label.excluded')})` : '') + ': '}
                            <b>{dropoffAreas.map((a, index, arr) => index === arr.length - 1 ? a : `${a}, `)}</b>
                        </Typography>
                    }
                    {pickupRegions.length > 0 &&
                        <Typography variant="body2">
                            {t('label.pickupRegions') + (excludedPickupRegions ? ` (${t('label.excluded')})` : '') + ': '}
                            <b>{pickupRegions.map((r, index, arr) => index === arr.length - 1 ? r : `${r}, `)}</b>
                        </Typography>
                    }
                    {dropoffRegions.length > 0 &&
                        <Typography variant="body2">
                            {t('label.dropoffRegions') + (excludedDropoffRegions ? ` (${t('label.excluded')})` : '') + ': '}
                            <b>{dropoffRegions.map((r, index, arr) => index === arr.length - 1 ? r : `${r}, `)}</b>
                        </Typography>
                    }
                    {pickupZones.length > 0 &&
                        <Typography variant="body2">
                            {t('label.pickupZones') + (excludedPickupZones ? ` (${t('label.excluded')})` : '') + ': '}
                            <b>{pickupZones.map((z, index, arr) => index === arr.length - 1 ? z : `${z}, `)}</b>
                        </Typography>
                    }
                    {dropoffZones.length > 0 &&
                        <Typography variant="body2">
                            {t('label.dropoffZones') + (excludedDropoffZones ? ` (${t('label.excluded')})` : '') + ': '}
                            <b>{dropoffZones.map((z, index, arr) => index === arr.length - 1 ? z : `${z}, `)}</b>
                        </Typography>
                    }
                    {acrissList.length > 0 &&
                        <Typography variant="body2">
                            {t('label.acrissList') + (excludedAcrissList ? ` (${t('label.excluded')})` : '') + ': '}
                            <b>{acrissList.map((a, index, arr) => index === arr.length - 1 ? a : `${a}, `)}</b>
                        </Typography>
                    }
                    {sippList.length > 0 &&
                        <Typography variant="body2">
                            {t('label.sippList') + (excludedSippList ? ` (${t('label.excluded')})` : '') + ': '}
                            <b>{sippList.map((s, index, arr) => index === arr.length - 1 ? s : `${s}, `)}</b>
                        </Typography>
                    }
                    {customerCodes.length > 0 &&
                        <Typography variant="body2">
                            {t('label.businessAgreements') + (excludedCustomerCodes ? ` (${t('label.excluded')})` : '') + ': '}
                            <b>{customerCodes.map((cc, index, arr) => index === arr.length - 1 ? cc : `${cc}, `)}</b>
                        </Typography>
                    }
                    {salesChannels.length > 0 &&
                        <Typography variant="body2">
                            {t('label.salesChannels') + (excludedSalesChannels ? ` (${t('label.excluded')})` : '') + ': '}
                            <b>
                                {salesChannels
                                    .reduce((prev: string[], curr) => {
                                        const founded = salesChannelsQuery.data?.find(v => v.code === curr)
                                        if (founded) {
                                            prev.push(founded.label)
                                        }
                                        return prev;
                                    }, [])
                                    .join(', ')
                                }
                            </b>
                        </Typography>
                    }
                    {segments.length > 0 &&
                        <Typography variant="body2">
                            {t('label.segments') + (excludedSegments ? ` (${t('label.excluded')})` : '') + ': '}
                            <b>
                                {segments
                                    .reduce((prev: string[], curr) => {
                                        const founded = segmentsQuery.data?.list.find(v => v.code === curr)
                                        if (founded) {
                                            prev.push(founded.label)
                                        }
                                        return prev;
                                    }, [])
                                    .join(', ')
                                }
                            </b>
                        </Typography>
                    }
                    {categories.length > 0 &&
                        <Typography variant="body2">
                            {t('label.categories') + (excludedCategories ? ` (${t('label.excluded')})` : '') + ': '}
                            <b>
                                {categories
                                    .reduce((prev: string[], curr) => {
                                        const founded = categoriesQuery.data?.list.find(v => v.code === curr)
                                        if (founded) {
                                            prev.push(founded.label)
                                        }
                                        return prev;
                                    }, [])
                                    .join(', ')
                                }
                            </b>
                        </Typography>
                    }
                    {products.length > 0 &&
                        <Typography variant="body2">
                            {t('label.products') + (excludedProducts ? ` (${t('label.excluded')})` : '') + ': '}
                            <b>
                                {products
                                    .reduce((prev: string[], curr) => {
                                        const founded = productsQuery.data?.list.find(v => v.code === curr)
                                        if (founded) {
                                            prev.push(founded.label)
                                        }
                                        return prev;
                                    }, [])
                                    .join(', ')
                                }
                            </b>
                        </Typography>
                    }
                    {rates.length > 0 &&
                        <Typography variant="body2">
                            {t('label.rates') + (excludedRates ? ` (${t('label.excluded')})` : '') + ': '}
                            <b>
                                {rates
                                    .reduce((prev: string[], curr) => {
                                        const founded = ratesQuery.data?.list.find(v => v.code === curr)
                                        if (founded) {
                                            prev.push(founded.label)
                                        }
                                        return prev;
                                    }, [])
                                    .join(', ')
                                }
                            </b>
                        </Typography>
                    }
                    {ancillaries.length > 0 &&
                        <Typography variant="body2">
                            {t('label.ancillaries') + (excludedAncillaries ? ` (${t('label.excluded')})` : '') + ': '}
                            <b>
                                {ancillaries
                                    .reduce((prev: string[], curr) => {
                                        const founded = ancillariesQuery.data?.list?.find(v => v.code === curr)
                                        if (founded) {
                                            prev.push(founded.label)
                                        }
                                        return prev;
                                    }, [])
                                    .join(', ')
                                }
                            </b>
                        </Typography>
                    }
                    {minDuration &&
                        <Typography variant="body2">
                            {t('label.minDuration') + (excludedMinDuration ? ` (${t('label.excluded')})` : '') + ': '}
                            <b>{minDuration}</b>
                        </Typography>
                    }
                    {maxDuration &&
                        <Typography variant="body2">
                            {t('label.maxDuration') + (excludedMaxDuration ? ` (${t('label.excluded')})` : '') + ': '}
                            <b>{maxDuration}</b>
                        </Typography>
                    }
                    {minNoticeDays &&
                        <Typography variant="body2">
                            {t('label.minNoticeDays') + (excludedMinNoticeDays ? ` (${t('label.excluded')})` : '') + ': '}
                            <b>{minNoticeDays}</b>
                        </Typography>
                    }
                    {maxNoticeHours &&
                        <Typography variant="body2">
                            {t('label.maxNoticeHours') + (excludedMaxNoticeHours ? ` (${t('label.excluded')})` : '') + ': '}
                            <b>{maxNoticeHours}</b>
                        </Typography>
                    }
                </Box>
            </Popover>
        </>
    );
};

export default CellParameters;
